import React, { useState, useEffect } from "react";
import { Calendar, X } from "lucide-react";

/**
 * mm/dd/yyyy → yyyy-mm-dd (ISO) or empty string if invalid.
 * Accepts leading zeros optional (e.g. 2/3/2026 or 02/03/2026).
 */
function mdyToIso(v) {
  if (!v) return "";
  const m = v.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  const [, mm, dd, yyyy] = m;
  const y = parseInt(yyyy, 10);
  const mo = parseInt(mm, 10);
  const d = parseInt(dd, 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  const iso = `${yyyy}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const parsed = new Date(iso + "T00:00:00");
  if (isNaN(parsed) || parsed.getMonth() + 1 !== mo || parsed.getDate() !== d) return "";
  return iso;
}

function isoToMdy(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[2]}/${m[3]}/${m[1]}`;
}

const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Compute the ISO {start, end} for a named preset. Ranges are inclusive.
 */
function computePreset(key) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case "this_month":
      return { start: toIso(new Date(y, m, 1)), end: toIso(new Date(y, m + 1, 0)) };
    case "last_30_days": {
      const end = new Date(now);
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { start: toIso(start), end: toIso(end) };
    }
    case "this_quarter": {
      const qStart = Math.floor(m / 3) * 3;
      return { start: toIso(new Date(y, qStart, 1)), end: toIso(new Date(y, qStart + 3, 0)) };
    }
    case "last_quarter": {
      const qStart = Math.floor(m / 3) * 3 - 3;
      const startY = qStart < 0 ? y - 1 : y;
      const startM = (qStart + 12) % 12;
      return { start: toIso(new Date(startY, startM, 1)), end: toIso(new Date(startY, startM + 3, 0)) };
    }
    case "this_year":
      return { start: toIso(new Date(y, 0, 1)), end: toIso(new Date(y, 11, 31)) };
    case "last_year":
      return { start: toIso(new Date(y - 1, 0, 1)), end: toIso(new Date(y - 1, 11, 31)) };
    default:
      return { start: "", end: "" };
  }
}

const PRESETS = [
  { key: "this_month", label: "This month" },
  { key: "last_30_days", label: "Last 30 days" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_quarter", label: "Last quarter" },
  { key: "this_year", label: "This year" },
  { key: "last_year", label: "Last year" },
];

/**
 * Compact controlled date-range picker.
 * `value` and `onChange` operate in ISO (YYYY-MM-DD) so parents don't have to
 * parse mm/dd/yyyy themselves. Empty strings mean "no range".
 */
export default function DateRangePicker({ value, onChange, compare, onCompareChange }) {
  const [from, setFrom] = useState(isoToMdy(value?.start || ""));
  const [to, setTo] = useState(isoToMdy(value?.end || ""));
  const [error, setError] = useState("");

  useEffect(() => {
    setFrom(isoToMdy(value?.start || ""));
    setTo(isoToMdy(value?.end || ""));
  }, [value?.start, value?.end]);

  const active = !!(value?.start && value?.end);

  const apply = () => {
    const s = mdyToIso(from);
    const e = mdyToIso(to);
    if (!s || !e) {
      setError("Enter both dates as mm/dd/yyyy");
      return;
    }
    if (s > e) {
      setError("Start date must be before end date");
      return;
    }
    setError("");
    onChange({ start: s, end: e });
  };

  const reset = () => {
    setFrom("");
    setTo("");
    setError("");
    onChange({ start: "", end: "" });
  };

  const applyPreset = (key) => {
    const r = computePreset(key);
    setError("");
    setFrom(isoToMdy(r.start));
    setTo(isoToMdy(r.end));
    onChange(r);
  };

  const activePreset = active
    ? PRESETS.find((p) => {
        const r = computePreset(p.key);
        return r.start === value.start && r.end === value.end;
      })?.key || null
    : null;

  return (
    <div
      className="card-surface p-4 mb-6 flex flex-col gap-3"
      data-testid="dashboard-date-range"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 shrink-0 text-ink">
          <Calendar className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-sm font-medium">Date range</span>
        </div>
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-ink-light">
            <span>From</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="mm/dd/yyyy"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="w-32 rounded-md border border-border px-2.5 py-1.5 text-sm font-mono text-ink focus:outline-none focus:ring-2 focus:ring-forest/25"
              data-testid="date-range-from"
              aria-label="From date"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-ink-light">
            <span>To</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="mm/dd/yyyy"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="w-32 rounded-md border border-border px-2.5 py-1.5 text-sm font-mono text-ink focus:outline-none focus:ring-2 focus:ring-forest/25"
              data-testid="date-range-to"
              aria-label="To date"
            />
          </label>
          <button
            type="button"
            onClick={apply}
            className="btn-forest text-sm px-3 py-1.5"
            data-testid="date-range-apply"
          >
            Apply
          </button>
          {active && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-xs text-ink-light hover:text-ink"
              data-testid="date-range-reset"
            >
              <X className="w-3.5 h-3.5" /> Reset
            </button>
          )}
          {error && (
            <span
              className="text-xs text-destructive"
              data-testid="date-range-error"
            >
              {error}
            </span>
          )}
          {active && !error && (
            <span
              className="text-xs text-forest ml-auto"
              data-testid="date-range-active-label"
            >
              Showing {isoToMdy(value.start)} → {isoToMdy(value.end)}
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-border/60"
        data-testid="date-range-presets"
      >
        <span className="text-[11px] uppercase tracking-wider text-ink-lighter mr-1">
          Quick
        </span>
        {PRESETS.map((p) => {
          const isActive = activePreset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                isActive
                  ? "bg-forest text-white border-forest"
                  : "border-border text-ink-light hover:bg-paper hover:text-ink"
              }`}
              data-testid={`date-range-preset-${p.key}`}
            >
              {p.label}
            </button>
          );
        })}
        {onCompareChange && (
          <label
            className={`ml-auto inline-flex items-center gap-2 text-xs cursor-pointer select-none rounded-full border px-2.5 py-1 transition-colors ${
              compare
                ? "bg-ochre/10 border-ochre text-ochre"
                : "border-border text-ink-light hover:bg-paper hover:text-ink"
            } ${!active ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="date-range-compare-label"
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={!!compare}
              disabled={!active}
              onChange={(e) => onCompareChange(e.target.checked)}
              data-testid="date-range-compare-toggle"
            />
            <span
              className={`w-2 h-2 rounded-full ${compare ? "bg-ochre" : "bg-ink-lighter/50"}`}
              aria-hidden="true"
            />
            Compare to prior period
          </label>
        )}
      </div>
    </div>
  );
}
